import { useCallback, useMemo, useState } from 'react'

import { LayoutRow, LayoutScroll } from 'components/LayoutPage'
import Form from 'components/controls/Form'
import SubmitButton from 'components/controls/SubmitButton/SubmitButton'
import Breadcrumbs from 'components/uiKit/Breadcrumbs'
import type { BreadcrumbItem } from 'components/uiKit/Breadcrumbs/Breadcrumbs'
import Button from 'components/uiKit/Button'
import { KitSize } from 'components/uiKit/KitTypes'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'components/uiKit/Modal'
import type { MCWithParams } from 'components/uiKit/Modal'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import Search from 'components/uiKit/Search'
import { useTableState } from 'components/uiKit/Table'
import { getDataGrouping, getTableErrorInfo } from 'components/uiKit/Table/utils'
import Tabs, { TabPanel } from 'components/uiKit/Tabs'
import { FileMetaFor } from 'gql/__generated__/graphql'
import { useFileMetaAndGroupMove } from 'gql/files/apollo'
import { t } from 'services/Translation'
import type { FinderItem } from 'store/models/entry.model'
import { getFilterByCreatedFor, isFinderFolder, useFinder } from 'utils/useFinder'

import * as s from './FileMove.module.scss'
import FileTable from './FolderTable'
import { MODAL_MOVE_FINDER_TABLE_NAME } from './FolderTable/FolderTable'

type IFileMoveParams = {
  companyId: string
  projectId: string
  fileMetaGroupId: string
  createdFor?: FileMetaFor.companies | FileMetaFor.projects
  movingItems: FinderItem[]
  displayTab?: boolean
}

const NAV_TABS_OPTIONS = [
  { title: t('modal.move.resources.tabs.project'), value: FileMetaFor.projects },
  { title: t('modal.move.resources.tabs.company'), value: FileMetaFor.companies },
]

const excludedIdsOptions = (excludedIds: FinderItem[]) =>
  excludedIds?.filter(isFinderFolder).map(({ id }) => id)

const FileMove: MCWithParams<IFileMoveParams> = ({ onClose, params }) => {
  const [search, setSearch] = useState<string>()
  const {
    companyId,
    projectId,
    fileMetaGroupId,
    createdFor = FileMetaFor.companies,
    movingItems,
    displayTab = false,
  } = params
  const [tab, setTab] = useState<FileMetaFor.companies | FileMetaFor.projects>(createdFor)
  const [targetGroupId, setTargetGroupId] = useState<string>(fileMetaGroupId)
  const [fileMetaAndGroupMove] = useFileMetaAndGroupMove()
  const { paginationConfig, pagination, sortConfig, order } = useTableState(
    MODAL_MOVE_FINDER_TABLE_NAME,
    { defaultSort: 'name' },
  )

  const { paramsMove, searchMove } = useMemo(() => {
    const paramsMove = {
      companyId,
      projectId,
      fileMetaGroupId: targetGroupId || 'root',
    }
    const searchMove = {
      fileMetaType: getFilterByCreatedFor(tab),
      excludedIds: excludedIdsOptions(movingItems),
      pagination,
      order,
      query: search,
    }
    return { paramsMove, searchMove }
  }, [companyId, projectId, targetGroupId, tab, movingItems, pagination, order, search])

  const { data, breadcrumbs, total, loading } = useFinder(paramsMove, searchMove, 'modalMove')

  const folders = useMemo(() => data.filter(isFinderFolder), [data])

  const isDisabledMove = useMemo(
    () =>
      (FileMetaFor.projects === tab && fileMetaGroupId === targetGroupId) ||
      (!fileMetaGroupId && !targetGroupId),
    [tab, fileMetaGroupId, targetGroupId],
  )

  const moveFiles = useCallback(async () => {
    const dataGrouping = getDataGrouping(movingItems)

    const dataMove = {
      ...dataGrouping,
      targetGroupId,
      currentGroupId: fileMetaGroupId,
      createdFor: tab,
    }

    try {
      const res = await fileMetaAndGroupMove({
        variables: { data: dataMove, companyId, projectId },
      })
      if (res.data?.data) {
        onClose()
        notify({
          type: NotificationType.success,
          message: t('notify.move', { count: movingItems?.length }),
        })
      }
    } catch (err) {
      onClose()
      notify({
        type: NotificationType.warning,
        message: t('notify.objectsMovingError'),
      })
      console.error(err)
    }
  }, [
    movingItems,
    targetGroupId,
    fileMetaGroupId,
    tab,
    fileMetaAndGroupMove,
    companyId,
    projectId,
    onClose,
  ])

  const handleClickBreadcrumbs = useCallback(
    (item: BreadcrumbItem) => setTargetGroupId(item.id || 'root'),
    [setTargetGroupId],
  )

  const onChangeTab = useCallback(
    (tab: FileMetaFor.companies | FileMetaFor.projects) => {
      setTab(tab)
      if (tab === FileMetaFor.projects) {
        setTargetGroupId(fileMetaGroupId)
      } else {
        setTargetGroupId('root')
      }
    },
    [fileMetaGroupId],
  )

  const breadcrumbsItems: BreadcrumbItem[] = useMemo(
    () => [
      { name: t('modal.move.resources'), id: 'root' },
      ...breadcrumbs.map(({ id, name }) => ({ name, id })),
    ],
    [breadcrumbs],
  )

  const ContentComponent = (
    <>
      <LayoutRow horizontalFree>
        <Search
          name='templatePickerSearch'
          onChange={setSearch}
          size={KitSize.S}
          value={search}
          wait={500}
          autoFocus
          fluid
        />
      </LayoutRow>
      <Breadcrumbs items={breadcrumbsItems} onClickBreadcrumbs={handleClickBreadcrumbs} />
      <div className={s.projectTable}>
        <LayoutScroll>
          <FileTable
            empty={getTableErrorInfo({
              disabled: {
                isError: isDisabledMove,
                text: <div>{t('modal.move.folder')}</div>,
              },
              search: { isError: Boolean(search) },
            })}
            folders={folders}
            isLoading={loading}
            paginationConfig={paginationConfig}
            setTargetGroupId={setTargetGroupId}
            sortConfig={sortConfig}
            totalFolders={total}
          />
        </LayoutScroll>
      </div>
    </>
  )

  return (
    <Form layout='vertical' name='fileMove' onSubmit={moveFiles}>
      <Modal maxWidth={800} name='fileMove'>
        <ModalHeader description={t('modal.move.destination')} title={t('modal.move.title')} />
        <ModalBody sizeAutoCapable>
          <div className={s.modalBody}>
            {displayTab && (
              <Tabs
                name='move'
                onChange={onChangeTab}
                size={KitSize.S}
                styleType='clear'
                grow
                tabsGrow
              >
                {NAV_TABS_OPTIONS.map(({ title, value }) => (
                  <TabPanel key={value} tabKey={value} title={title}>
                    {ContentComponent}
                  </TabPanel>
                ))}
              </Tabs>
            )}
            {!displayTab && ContentComponent}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button name='cancel' onClick={() => onClose()} styleType='ghost'>
            {t('uiKit.button.cancel')}
          </Button>
          <SubmitButton disabled={isDisabledMove} label={t('uiKit.button.move')} name='move' />
        </ModalFooter>
      </Modal>
    </Form>
  )
}

export default FileMove
